/**
 * useLeafletPlugins — Sprint 4.2B.
 *
 * Carrega plugins Leaflet via CDN (idempotente):
 *   - leaflet.heat 0.2.0 (heatmaps)
 *   - leaflet.markercluster 1.5.3 (clustering)
 *
 * Premissa: window.L já deve estar disponível (chamar useLeaflet antes).
 *
 * Retorna { heatReady, clusterReady, error } — ambos são bool.
 *
 * Plugins anexam métodos a L: L.heatLayer(...) e L.markerClusterGroup(...).
 */
import { useEffect, useState } from "react";

const HEAT_JS = "https://unpkg.com/leaflet.heat@0.2.0/dist/leaflet-heat.js";
const CLUSTER_CSS = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css";
const CLUSTER_CSS_DARK = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css";
const CLUSTER_JS = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js";

function injectCss(href, dataKey) {
  if (document.querySelector(`link[data-plugin="${dataKey}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.dataset.plugin = dataKey;
  document.head.appendChild(link);
}

function injectScript(src, dataKey, check) {
  return new Promise((resolve, reject) => {
    if (check()) return resolve(true);
    const existing = document.querySelector(`script[data-plugin="${dataKey}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(check()));
      existing.addEventListener("error", () => reject(new Error(`Falha ao carregar ${dataKey}`)));
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.dataset.plugin = dataKey;
    s.onload = () => resolve(check());
    s.onerror = () => reject(new Error(`Falha ao carregar ${dataKey}`));
    document.head.appendChild(s);
  });
}

export function useLeafletPlugins(L) {
  const [heatReady, setHeatReady] = useState(
    !!(typeof window !== "undefined" && window.L?.heatLayer)
  );
  const [clusterReady, setClusterReady] = useState(
    !!(typeof window !== "undefined" && window.L?.markerClusterGroup)
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!L) return;
    let cancelled = false;

    // leaflet.heat
    if (!L.heatLayer) {
      injectScript(HEAT_JS, "leaflet-heat", () => !!window.L?.heatLayer)
        .then((ok) => {
          if (!cancelled) setHeatReady(!!ok);
        })
        .catch((e) => {
          if (!cancelled) setError(e);
        });
    } else {
      setHeatReady(true);
    }

    // leaflet.markercluster
    if (!L.markerClusterGroup) {
      injectCss(CLUSTER_CSS, "cluster-css");
      injectCss(CLUSTER_CSS_DARK, "cluster-css-default");
      injectScript(CLUSTER_JS, "leaflet-markercluster", () => !!window.L?.markerClusterGroup)
        .then((ok) => {
          if (!cancelled) setClusterReady(!!ok);
        })
        .catch((e) => {
          if (!cancelled) setError(e);
        });
    } else {
      setClusterReady(true);
    }

    return () => { cancelled = true; };
  }, [L]);

  return { heatReady, clusterReady, error };
}
